/**
 * constants.js — Shared tuning values for Sky-Drop.
 *
 * These were previously declared inline in App.js. Extracted so gameplay
 * modules (physics, AI, rendering) can share a single source of truth for the
 * canvas dimensions and physics feel without threading them through closures.
 */

/**
 * How wide each screen's play area is, in world px — the arena's shape, and the
 * reason a match gets harder the deeper it is pushed.
 *
 * Every screen used to be 800 wide, which meant crossing the last one took
 * exactly as long as crossing the first: measured against a passive opponent, a
 * whole match was ~15s of which ~10s was uncontested running, identical at every
 * AI difficulty (see tests/e2e/test19_match_pacing.js). One kill bought the rest
 * of the game because the rest of the game was a fixed-length sprint.
 *
 * So the arena widens outward from the middle. The landing stage is the 800 it
 * always was; each step toward a goal is a longer room with more in it, and the
 * goal screens are more than twice the landing stage. Pushing into someone's
 * territory now costs real time and real ground, and the defender gets that time
 * back to turn the match around. This is Nidhogg's shape, which is the game
 * Sky-Drop's tug-of-war came from.
 *
 * Indexed by screen number, symmetric about the middle so neither side is
 * favoured. THIS TABLE IS THE ONLY PLACE THE ARENA'S WIDTH IS STATED. Nothing
 * else may assume a width; ask screenWidth() for the screen you are drawing,
 * simulating or steering in.
 */
export const SCREEN_WIDTHS = [1800, 1200, 1000, 800, 1000, 1200, 1800];

/** Number of screens in a match. */
export const SCREEN_COUNT = SCREEN_WIDTHS.length;

/** The middle screen, where every match starts. */
export const START_SCREEN = (SCREEN_COUNT - 1) / 2;

/**
 * @param {number} screen Screen index.
 * @returns {number} that screen's play-area width in world px.
 */
export const screenWidth = (screen) => SCREEN_WIDTHS[screen] ?? MIN_SCREEN_WIDTH;

/**
 * The narrowest and widest rooms in the game.
 *
 * The narrowest is what the FRAME is sized against (viewport.js), because the
 * frame's job is to be at least as wide as the smallest room — beyond that the
 * camera pulls back rather than the frame growing. The widest is what bounds how
 * far the camera can ever be pulled back (camera.js).
 */
export const MIN_SCREEN_WIDTH = Math.min(...SCREEN_WIDTHS);
export const MAX_SCREEN_WIDTH = Math.max(...SCREEN_WIDTHS);

// Fixed logical canvas height. The rendered element is scaled to the viewport in
// CSS; all game math is done against this intrinsic dimension. There is
// deliberately no CANVAS_WIDTH to match it — width is a property of the screen
// you are on, not of the game.
export const CANVAS_HEIGHT = 600;

// Physics feel.
export const GRAVITY = 0.5;
export const FRICTION = 0.9;
// Was 8. Brought down on player feedback that the board was outrunning
// people's reactions — not the beam, not the fists, the rate the rider
// crosses the screen. Everything that reads as "how hard is the board being
// pushed" (steering limits, the carve pose, the lean) is a ratio of this, so
// it all scales together. The kicked-board projectile and the jump do not
// depend on it and are unchanged.
export const BOARD_SPEED = 7;
// Frames from rest to top speed. A frame COUNT rather than an acceleration on
// purpose: retuning BOARD_SPEED then keeps the handling identical and only
// moves the ceiling. Written as a raw acceleration, a slower board would have
// reached its cap in fewer frames and felt twitchier, not gentler — the
// opposite of what a speed reduction is for.
export const BOARD_RAMP_FRAMES = 10;
export const BOARD_ACCEL = BOARD_SPEED / BOARD_RAMP_FRAMES;
// Vertical cap while hovering. Used to share BOARD_SPEED, which was the bug
// behind "pressing down is too fast": a board is a lateral vehicle, and
// climbing or diving at full skating speed crossed the whole arena height in
// under a second. Descent is also naturally the quicker direction, since hover
// gravity (0.15/frame) adds to a downward push and subtracts from an upward
// one — this cap is what the player actually feels, and it now bounds both.
export const BOARD_CLIMB_SPEED = 4.5;
export const PLAYER_SPEED = 5;
export const JUMP_FORCE = -12;

// Milliseconds before a fired beam can be fired again (Hands mode).
export const BEAM_RECHARGE_TIME = 6000;

/**
 * Fixed simulation timestep. The game integrates physics and counts down all
 * timers in whole steps of this size, driven by an accumulator in the render
 * loop — so a 144Hz display runs the same simulation speed as a 60Hz one.
 * Every tuning value below is expressed in milliseconds of simulated time.
 */
export const STEP_MS = 1000 / 60;

/**
 * Ceiling on catch-up steps per animation frame. Without this, returning to a
 * backgrounded tab would hand the accumulator several seconds of delta and the
 * simulation would fast-forward through it in one frame.
 */
export const MAX_CATCHUP_STEPS = 5;

// Play-area bounds. player.y is the character's FEET, so the floor is where
// feet rest and the ceiling leaves room for the tallest sprite's head.
export const GROUND_Y = CANVAS_HEIGHT - 100;
export const CEILING_Y = 120;

/**
 * Spawn positions.
 *
 * x is a FRACTION of the screen's width rather than a coordinate, because the
 * screens are no longer the same width: a fixed 150 puts a fighter a fifth of
 * the way into the landing stage and a twelfth of the way into a goal screen.
 * As a fraction, each fighter always spawns at their own end of whatever room
 * they are in. (The values are the old 150 and 650 over the old 800.)
 *
 * The two heights are deliberately staggered: when both players spawned at the
 * same y they were perfectly beam-aligned from frame one, which made the opening
 * a race to Hands mode rather than a fight.
 */
export const SPAWN = {
  player1: { xFraction: 150 / 800, y: 360 },
  player2: { xFraction: 650 / 800, y: 440 },
};

/**
 * Where a fighter spawns on a given screen.
 *
 * @param {'player1'|'player2'} slot
 * @param {number} screen
 * @returns {{x: number, y: number}} world position of their feet.
 */
export const spawnAt = (slot, screen) => ({
  x: SPAWN[slot].xFraction * screenWidth(screen),
  y: SPAWN[slot].y,
});

/**
 * Eye level, and therefore the horizon — the single line the whole scene's
 * perspective is built around.
 *
 * Two things have to agree about where the viewer's eye is, and they were
 * disagreeing by 263px: the containment cell converges on its vanishing point,
 * and the city converges on its horizon. When those sit at different heights
 * the box reads as seen from above while the city reads as seen from deck
 * level, and no amount of tuning either one in isolation fixes it.
 *
 * Placed at a standing fighter's head height, which is the natural camera
 * position for a side-on duel: BODY_TOP above the ground line.
 */
/**
 * Character body box for combat collisions, relative to (player.x, player.y)
 * where y is the feet line. Beams, boards and hand-to-hand all test against
 * this same box so every weapon agrees on what "hitting someone" means.
 */
export const BODY_HALF_WIDTH = 13;
export const BODY_TOP = -52; // top of the head, above the feet
export const BODY_BOTTOM = 8; // just under the feet

/**
 * Eye level, and therefore the horizon — the single line the whole scene's
 * perspective is built around.
 *
 * Two things have to agree about where the viewer's eye is, and they were
 * disagreeing by 263px: the containment cell converges on its vanishing point,
 * and the city converges on its horizon. With those at different heights the
 * box reads as seen from well above the fighters while the city reads as seen
 * from down at deck level, and tuning either one alone cannot reconcile them.
 *
 * Put at a standing fighter's head height, which is the natural camera position
 * for a side-on duel.
 */
export const HORIZON_Y = GROUND_Y + BODY_TOP;

/**
 * Spacing of the deck's plating seams, in world px along the floor line. Wide
 * enough that the seams read as floor panels rather than a grid pattern; the
 * perspective fans them out toward the viewer on its own.
 */
export const DECK_SEAM_SPACING = 96;

/**
 * The containment cell's wall portals.
 *
 * Each side wall opens only through a portal floating at mid-height, so
 * crossing into the next screen means LINING UP with it — a rider flies
 * through; a grounded fighter's full jump peaks inside the band (apex feet
 * height is GROUND_Y - JUMP_FORCE^2 / (2 * GRAVITY) = 356, inside the sill),
 * so nobody is locked out, but arriving at the wrong height presses against
 * glass. The gate is shared by the screen-advancement check, the AI's
 * approach behavior, and the cell renderer, so what is drawn, what is
 * enforced, and what the AI aims for are the same portal.
 */
export const CELL_DOOR_TOP_Y = 180; // portal top, in world y
export const CELL_DOOR_BOTTOM_Y = 380; // portal sill, in world y
export const CELL_DOOR_HEADROOM = 56; // tallest pose reaches this far above player.y

/**
 * Whether a fighter at this feet height fits through the portal band.
 *
 * @param {number} feetY player.y (the feet line)
 * @returns {boolean}
 */
export const fitsThroughCellDoor = (feetY) =>
  feetY <= CELL_DOOR_BOTTOM_Y &&
  feetY - CELL_DOOR_HEADROOM >= CELL_DOOR_TOP_Y;

// Loose-board box, relative to the board's centre.
export const BOARD_HALF_WIDTH = 18;
export const BOARD_HALF_HEIGHT = 6;

/**
 * Where a beam leaves the board when it is held out as a cannon, relative to
 * the player's feet (dx is measured forward from centre and mirrors with
 * facing). This is gameplay geometry, not just drawing geometry: the renderer
 * spawns the shot at the sprite's barrel tip and the AI has to aim that same
 * line at an opponent's body, so both read it from here.
 *
 * The board is a separate sprite now, held out ahead of the braced firing
 * stance, so the nose reaches further forward and sits at chest height rather
 * than over the head. sprites.js positions the drawn board to put its nose on
 * exactly this point — see HELD_BOARD.
 */
export const MUZZLE_DX = 40;
export const MUZZLE_DY = -32;

/**
 * Beam windup. Firing is committed the moment it starts (the shot goes off
 * even if the key is released), and the muzzle visibly charges for this long
 * first — the tell that makes a full-screen instant kill dodgeable instead of
 * a pure reflex check.
 */
export const BEAM_CHARGE_TIME = 280;

/** Beam dwell time on screen once fired. */
export const BEAM_LIFE = 500;

/**
 * Invulnerability granted on respawn and on being pushed into a new screen.
 * Long enough to leave the spawn point before a camped beam can connect.
 */
export const SPAWN_INVULN_TIME = 1200;

/**
 * Handling transitions, in milliseconds.
 *
 * Swapping the board between underfoot and the firing grip used to be a single
 * frame: the board teleported from one to the other and the rider never moved.
 * These give the move a duration to play over, and DRAW_TIME additionally gates
 * the beam — you cannot bring the board up and fire in the same instant, so
 * switching to Hands in someone's face is a commitment rather than a free
 * option. Kept short enough that it never feels like input lag.
 */
export const DRAW_TIME = 300;
export const STOW_TIME = 240;
export const MOUNT_TIME = 260;

/** Duration of the kick backflip animation. */
export const BACKFLIP_TIME = 1000;

/** Duration of the vaporization death animation before respawn. */
export const VAPORIZE_TIME = 1000;

/**
 * How close a fighter must be to step back onto their board.
 *
 * Shared with the AI, which has to know exactly when a mount will succeed: it
 * walks to its board and then presses the same action key a human would. When
 * this lived only in App.js the AI carried its own guess at the value, stopped
 * short of it, and stood next to its board indefinitely.
 */
export const BOARD_MOUNT_RANGE = 50;

/** Stun applied when grabbing an opponent's board. */
export const SHOCK_STUN_TIME = 1000;

/**
 * Simulation freeze applied on a kill. A few frames of dead air make the hit
 * register as an event rather than a state change that already happened.
 */
export const HIT_STOP_TIME = 90;

/**
 * Screen-change dolly. The camera travels from the old room into the new one
 * over TIME milliseconds — a full arena of ground, so shorter than ~700ms
 * reads as a lurch — and the simulation holds for the first HOLD of those so
 * the breakthrough lands as a moment rather than a hard cut. The hold is
 * deliberately shorter than the travel: play resumes while the camera is
 * still arriving, which is most of what makes it feel like a broadcast
 * cameraman chasing the fight rather than a scene load.
 */
export const SCREEN_TRANSITION_TIME = 850;
export const SCREEN_TRANSITION_HOLD = 170;

/* ===========================================================================
   Ordnance — cluster bombs and the EMP
   ===========================================================================
   Two thrown devices, and NEITHER of them kills. That is the whole point of
   them, and it is why they are a new dynamic rather than two more weapons.

   Everything else in the arsenal is flat, horizontal and decisive: the beam is
   a hitscan line, the kicked board is a flat projectile, and both end a life
   outright. So every fight is the same fight — hold a firing line or break it,
   with cover as the only currency. Ordnance is the answer to a fighter who has
   simply parked behind a slab: it ARCS, so it goes over the thing they are
   hiding behind, and it takes their footing rather than their life.

   The cluster bomb stuns and knocks a rider off their board — an opening, paid
   off with something else. The EMP kills the board itself, which drops its
   rider into the hand-to-hand game the melee system already implements and
   which is otherwise hard to reach, because getting somebody off a board and
   keeping them off it is hard.

   Both are thrown with the action button, from the board — see the note on
   ORDNANCE_KINDS. Blasts catch EVERYONE in radius including whoever threw it,
   which is the ordinary rule for a grenade and is what makes the throw a
   decision about spacing rather than a free button.
   -------------------------------------------------------------------------- */

/** The two kinds, as they appear in state and on the wire. */
export const ORDNANCE_KINDS = ['cluster', 'emp'];

/**
 * The loadout. Every fighter carries both, from the first frame of the match —
 * no pickups, nothing to fetch — and each returns on its own clock after use.
 *
 * The cluster is a tool: three of them, one back every minute, so it is
 * something you spend and keep spending. The EMP is an event: one, and three
 * minutes to get it back, which is longer than most matches last. That
 * asymmetry is the design — the pulse is a decision you make once and have to
 * be right about, and the shells are how you make the position for it.
 */
export const CLUSTER_MAX = 3;
export const CLUSTER_RECHARGE = 60000;
export const EMP_MAX = 1;
export const EMP_RECHARGE = 180000;

/**
 * The throw. Lofted rather than flung: the arc is the feature, so it has to be
 * high enough to clear a barrier and slow enough to be read and stepped away
 * from. It peaks about 100px over the deck, which clears a floor barrier (74)
 * and does not clear a billboard — a shell that hits tall cover bursts against
 * it, which is a fair answer to hiding behind something that tall.
 *
 * SPEED IS SET BY WHERE IT HAS TO LAND, and getting that wrong is what makes an
 * arcing weapon useless. There is no aiming: every throw travels the same
 * distance, so that distance has to be the distance fights happen at. At the
 * first value tried a shell flew about 590px and sailed clean over anybody
 * closer than five hundred — a weapon whose minimum range was longer than the
 * average engagement. At this speed it lands around 380px out and the
 * submunitions scatter either side of it, into a beaten zone roughly 200-700px
 * from the thrower, which covers both the beam duel and the closing game.
 */
export const THROW_SPEED = 8.5;
export const THROW_LIFT = -7;
export const ORDNANCE_GRAVITY = 0.34;

/**
 * Fuses, in ms. A device bursts on whatever it touches first — deck, obstacle,
 * wall or fighter — and on the fuse if it touches nothing at all, so nothing
 * thrown is ever simply lost.
 */
export const CLUSTER_FUSE = 900;

/**
 * The cluster's second stage: the shell splits into submunitions that scatter
 * along its travel and go off shortly after, so the danger is a WIDTH of deck
 * rather than a point. That width is the reason it beats cover.
 */
export const CLUSTER_SUBMUNITIONS = 5;
export const SUBMUNITION_FUSE = 420;
export const SUBMUNITION_SPREAD = 5.5;

/** Cluster blast reach, in px from the burst to a fighter's centre. */
export const CLUSTER_BLAST_RADIUS = 104;

/**
 * The EMP is NOT thrown. It emanates from the fighter who fires it and sweeps
 * outward to the edges of the whole stage, so there is no aiming, no arc, no
 * fuse and nowhere to stand that is out of it.
 *
 * That is the right shape for a weapon you get once every three minutes. An
 * arcing device with a blast radius is a thing you can miss with, and missing
 * with something you get once a match is not a skill test, it is a wasted
 * match. A pulse that fills the arena cannot miss — what it costs is the
 * decision of WHEN, which is the interesting question and the only one.
 *
 * It does not touch the board of whoever fired it. With two fighters, a pulse
 * that killed both boards would be perfectly symmetrical and therefore worth
 * nothing.
 *
 * The sweep is a travel time, not a delay: the wavefront crosses the arena over
 * this long and a board dies as the edge reaches it, so on the widest screens
 * there is a real moment between firing and the far side going dark.
 */
export const EMP_SWEEP_TIME = 620;

/**
 * What a cluster blast does: takes your feet out. The stun is long enough to be
 * an opening somebody else has to convert, and short enough that it is not
 * itself the kill — a stunned fighter cannot act (see getAIInput and
 * handlePlayerInput, both of which already honour player.stunned).
 */
export const CLUSTER_STUN_TIME = 1400;

/**
 * What an EMP blast does: the board stops being a board.
 *
 * Thirty seconds is most of a match — measured, a full match against a passive
 * opponent runs about twenty. So this does not inconvenience a rider, it puts
 * them on foot for the rest of the fight, where the melee system decides
 * things and where a thrown board kills them outright. Deliberately the
 * heaviest thing in the game that is still not a kill.
 *
 * Cleared by dying (see completeRespawn), because a respawn hands back a fresh
 * board and stacking the two punishments would make one EMP worth more than a
 * kill.
 */
export const EMP_BOARD_DOWN_TIME = 30000;

/** How long a blast ring is drawn for. Presentation only. */
export const BLAST_LIFE = 420;

/**
 * Double-tap window for slinging the board onto your back, in ms.
 *
 * Hand-to-hand only ever happened TO you before this: your board had to be
 * kicked away or killed by a pulse. A whole combat system was reachable only by
 * accident. This is the way in — you choose to put the board away and fight.
 *
 * On the guard button, because guard is already the "I am on foot" control and a
 * double tap is free there: a single one is a hold. One scheme means that is
 * Shift on a keyboard, the guard pad on a phone and the left trigger on a pad,
 * with nothing to specify per device.
 */
export const HOLSTER_TAP_WINDOW = 300;
